import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReasoningEffort, ReasoningSearchState, ReasoningSession } from "~/api/reasoningTypes";
import { contextManifest } from "~/features/reasoning/reasoningSelectors";
import { useReasoningSupport } from "~/features/reasoning/useReasoningSupport";
import { Button } from "~/foundation/components/Button";
import type { ActionSpec } from "./usePendingAction";

export function ReasoningSearchSection({
  session,
  thinker,
  evaluator,
  effort,
  busy,
  act,
}: {
  session: ReasoningSession;
  thinker?: { id: string; model: string };
  evaluator?: { id: string; model: string };
  effort: ReasoningEffort;
  busy: boolean;
  act: (action: ActionSpec) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { limits } = useReasoningSupport();
  const [mode, setMode] = useState<"tree" | "graph">("tree");
  const [count, setCount] = useState(8);
  const [width, setWidth] = useState(2);
  const [depth, setDepth] = useState(3);
  const record = session.records
    .filter((row) => row.kind === "search-checkpoint")
    .sort((a, b) => b.revision - a.revision)[0];
  const state = record?.payload as unknown as ReasoningSearchState | undefined;
  if (!state && session.records.some((row) => row.kind === "candidate")) return null;
  if (!limits.maxSearchCandidates) return null;
  const bindingMatches = thinker?.model === contextManifest(session)?.model;
  const valid =
    thinker &&
    evaluator &&
    bindingMatches &&
    count >= 1 &&
    count <= limits.maxSearchCandidates &&
    width >= 1 &&
    width <= limits.maxSearchWidth &&
    depth >= 1 &&
    depth <= limits.maxSearchDepth;
  const status =
    state?.status === "selected-proposal"
      ? t("A reviewed proposal is ready")
      : state?.status === "not-found-within-limits"
        ? t("No solution found within these limits")
        : state?.status === "budget-limited"
          ? t("Search stopped at its budget limit")
          : state?.status === "stale"
            ? t("Search inputs changed; start a new session")
            : state?.status === "uncertain"
              ? t("A model call is uncertain; review its receipt before continuing")
              : state?.status === "failed"
                ? t("Search failed; inspect the retained receipt")
                : state?.status === "stopped"
                  ? t("Search stopped")
                  : t("Search is ready for its next step");
  return (
    <section className="flex flex-col gap-3 rounded border border-border-primary p-3">
      <h3 className="font-medium">{t("Explore alternatives")}</h3>
      <p className="text-small text-text-secondary">
        {t(
          "Each step generates one alternative and reviews it against your criteria. Acceptance remains your decision.",
        )}
      </p>
      {!state ? (
        <>
          <label>
            {t("Search structure")}
            <select
              aria-label={t("Search structure")}
              value={mode}
              disabled={busy}
              onChange={(event) => setMode(event.target.value as "tree" | "graph")}
            >
              <option value="tree">{t("Tree: separate alternatives")}</option>
              <option value="graph">{t("Graph: combine compatible alternatives")}</option>
            </select>
          </label>
          <label>
            {t("Candidate limit")}
            <input
              type="number"
              min={1}
              max={limits.maxSearchCandidates}
              value={count}
              disabled={busy}
              onChange={(event) => setCount(Number(event.target.value))}
            />
          </label>
          <label>
            {t("Alternatives per step")}
            <input
              type="number"
              min={1}
              max={limits.maxSearchWidth}
              value={width}
              disabled={busy}
              onChange={(event) => setWidth(Number(event.target.value))}
            />
          </label>
          <label>
            {t("Maximum depth")}
            <input
              type="number"
              min={1}
              max={limits.maxSearchDepth}
              value={depth}
              disabled={busy}
              onChange={(event) => setDepth(Number(event.target.value))}
            />
          </label>
          {!bindingMatches && (
            <p>{t("Choose the model pinned by this session to start a search.")}</p>
          )}
          <Button
            disabled={busy || !valid}
            onClick={() => {
              if (!thinker || !evaluator || !valid) return;
              void act({
                kind: "search-start",
                input: {
                  sessionId: session.id,
                  expectedSessionRevision: session.revision,
                  options: {
                    mode,
                    thinkerId: thinker.id,
                    model: thinker.model,
                    evaluatorThinkerId: evaluator.id,
                    evaluatorModel: evaluator.model,
                    effort,
                    maxCandidates: count,
                    width,
                    maxDepth: depth,
                  },
                },
              });
            }}
          >
            {t("Configure search")}
          </Button>
        </>
      ) : (
        <>
          <p role="status">{status}</p>
          <p>
            {t("{{count}} candidates reviewed; {{remaining}} alternatives remain", {
              count: state.candidates.length,
              remaining: state.frontier.length,
            })}
          </p>
          {state.error && <p role="alert">{state.error}</p>}
          {state.status === "searching" && (
            <Button
              disabled={busy}
              onClick={() => void act({ kind: "search-advance", input: { sessionId: session.id } })}
            >
              {state.pending ? t("Resume the retained search step") : t("Explore next alternative")}
            </Button>
          )}
          {state.selectedCandidateId && (
            <p>{t("Review the selected proposal before accepting it.")}</p>
          )}
        </>
      )}
    </section>
  );
}
